import { calculateEMA } from './chartUtils';
import { getToken } from './tokenManager'
import {
  isCryptoSymbol,
  fetchCryptoAggregates,
  fetchCryptoPrice,
  fetchCryptoPrices,
  fetchCryptoName,
  getStoredCryptoDetails,
} from './fetchCrypto';
const POLYGON_API_KEY = process.env.REACT_APP_POLYGON_API_KEY;
const GATEWAY_BASE_URL = 'https://gateway.wethaiinvest.com';
const API = 'https://api.wethaiinvest.com';

export interface StockMarketInfo {
  price: number;
  regularClose: number;
  extendedPrice: number | null;
  marketSession: string | null;
}
// regularClose = ราคาปิดช่วงตลาดปกติ ใช้เป็นฐานคำนวณส่วนต่างของราคาก่อนตลาดเปิด/หลังตลาดปิด
const marketDetailsCache: { [symbol: string]: { marketSession: string | null; extendedPrice: number | null; regularClose?: number | null; providerTimestampIso?: string | null } } = {};

export async function fetchPolygonLastTrade(symbol: string): Promise<number | null> {
  try {
    const authToken = getToken();
    const res = await fetch(
      `${GATEWAY_BASE_URL}/v1/price/v2/last/trade/${symbol}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    const data = await res.json();

    // ปรับตามโครงสร้างรีสพอนส์จริงของ endpoint นี้ (หากรูปแบบเปลี่ยน)
    if (data?.results && typeof data.results.p === 'number') {
      return data.results.p;
    }
    return null;
  } catch (err) {
    return null;
  }
}

// export async function fetchOHLC(
//   symbol: string,
//   resolution: 'minute' | 'hour' | 'day' | 'week' | 'month', // <-- add week and month
//   multiplier: number,
//   from: string,
//   to: string
// ): Promise<any[]> {
//   try {
//     const res = await fetch(
//       `https://api.massive.com/v2/aggs/ticker/${symbol}/range/${multiplier}/${resolution}/${from}/${to}?adjusted=true&sort=asc&limit=50000`,
//       {
//         headers: {
//           Authorization: `Bearer ${POLYGON_API_KEY}`,
//         },
//       }
//     );
//     const data = await res.json();
//     return data.results || [];
//   } catch (err) {
//     return [];
//   }
// }
export async function fetchOHLC(
  symbol: string,
  resolution: 'minute' | 'hour' | 'day' | 'week' | 'month',
  multiplier: number,
  from: string,
  to: string
): Promise<any[]> {
  // Crypto ไม่มี intraday ให้ใช้รายวันแทน minute/hour
  if (isCryptoSymbol(symbol)) {
    const cryptoTimeframe = resolution === 'week' || resolution === 'month' ? resolution : 'day';
    return fetchCryptoAggregates(symbol, cryptoTimeframe, from, to);
  }

  try {
    const authToken = getToken();

    // แปลง resolution และ range ให้ตรงกับที่ Endpoint ใหม่รองรับ (ตัวอย่างเช่น timeframe=DAY หรือใช้ range ตามที่ส่งมา)
    // หรือถ้า Backend ออกแบบไว้ให้ส่ง range เช่น '10Y' และ timeframe เช่น 'DAY' สามารถปรับตามนี้ได้เลยครับ:
    const timeframeMap: Record<string, string> = {
      minute: 'MINUTE',
      hour: 'HOUR',
      day: 'DAY',
      week: 'WEEK',
      month: 'MONTH'
    };
    const timeframe = timeframeMap[resolution] || 'DAY';

    // ตัวอย่างการส่ง Query Parameters ไปยัง Endpoint ใหม่
    const url = `${GATEWAY_BASE_URL}/v1/stocks/${symbol}/aggregates?range=10Y&timeframe=${timeframe}`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    const data = await res.json();
    return data?.data?.results || data.results || [];
  } catch (err) {
    console.error('Error fetching OHLC via gateway:', err);
    return [];
  }
}

export async function fetchUsStockSymbols(query: string): Promise<any[]> {
  try {
    if (!query || query.trim() === '') return [];

    const authToken = getToken();
    const url = `${API}/v2/master-assets?search=${encodeURIComponent(query)}&ticker=${encodeURIComponent(query)}&limit=10`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`Gateway error: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();

    // ✅ แกะโครงสร้างตามจริง: ดึงจาก data.data.items
    const items = data?.data?.items || data?.results || data?.data?.results || [];

    return items.map((item: any) => ({
      symbol: item.ticker || item.assetSymbol,
      name: item.displayName || item.shortName || item.name
    }));
  } catch (err) {
    console.error('Error fetching stock symbols via gateway:', err);
    return [];
  }
}

export async function fetchHistoricalDataForEMA(
  symbol: string
): Promise<any[]> {
  if (isCryptoSymbol(symbol)) {
    const to = new Date();
    const from = new Date(to);
    from.setUTCFullYear(to.getUTCFullYear() - 10);
    return fetchCryptoAggregates(symbol, 'day', from.toISOString().slice(0, 10), to.toISOString().slice(0, 10));
  }

  try {
    const authToken = getToken();
    // ดึงข้อมูลย้อนหลัง 5 ปีด้วย timeframe แบบ DAY ตามโครงสร้างใหม่
    const url = `${GATEWAY_BASE_URL}/v1/stocks/${symbol}/aggregates?range=10Y&timeframe=DAY`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`Gateway error: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    return data?.data?.results || data.results || [];
  } catch (err) {
    console.error('❌ Error fetching historical data for EMA via gateway:', err);
    return [];
  }
}

export async function fetchPolygonSnapshotPrice(symbol: string): Promise<number | null> {
  if (isCryptoSymbol(symbol)) return fetchCryptoPrice(symbol);

  try {
    const authToken = getToken();
    const res = await fetch(
      `${GATEWAY_BASE_URL}/v1/stocks/v2/snapshot/locale/us/markets/stocks/tickers/${symbol}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    const data = await res.json();

   if (data.success && data.results && data.results.length > 0) {
      const tickerData = data.results[0];
      
      // 🎯 เอา regular_close ขึ้นมาไว้บนสุดเช่นกัน
      if (tickerData.price?.regular_close && tickerData.price.regular_close > 0) {
        return tickerData.price.regular_close;
      }
      if (tickerData.price?.price && tickerData.price.price > 0) {
        return tickerData.price.price;
      }
      if (tickerData.price?.extended_price && tickerData.price.extended_price > 0) {
        return tickerData.price.extended_price;
      }
      if (tickerData.day?.c && tickerData.day.c > 0) {
        return tickerData.day.c;
      }
      if (tickerData.prevDay?.c && tickerData.prevDay.c > 0) {
        return tickerData.prevDay.c;
      }
    }
    return null;
  } catch (err) {
    console.error('Error fetching snapshot price via gateway:', err);
    return null;
  }
}

// export async function fetchPolygonSnapshotPriceses(
//   symbols: string[], 
//   retries = 3 // กำหนดให้ลองใหม่ได้สูงสุด 3 ครั้ง
// ): Promise<{ [symbol: string]: number }> {
//   try {
//     const prices: { [symbol: string]: number } = {};
//     if (symbols.length === 0) return prices;

//     const symbolQuery = symbols.join(',');
//     const res = await fetch(
//       `https://api.massive.com/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${symbolQuery}&apiKey=${POLYGON_API_KEY}`
//     );
//     const data = await res.json();

//     // เช็คว่า API ตัวนี้ส่งข้อมูลเอ๋อ (updated เป็น 0) หรือไม่มี tickers โผล่มาไหม
//     // หรือเช็คจากตัวแรกในลิสต์ก็ได้ว่า day.c เป็น 0 หรือเปล่า
//     const isDataInvalid = !data.tickers || data.tickers.length === 0 || data.tickers[0]?.updated === 0;

//     // 🔥 ถ้าเจอข้อมูลที่เป็น 0 และยังมีโควตาเหลือ (retries > 0) ให้สั่งยิงใหม่ทันที!
//     if (isDataInvalid && retries > 0) {
//       // console.warn(`เจอข้อมูลเอ๋อ (ค่าเป็น 0), กำลังลอง Fetch ซ้ำใหม่... เหลือโควตาอีก ${retries} ครั้ง`);
//       // หน่วงเวลาแป๊บหนึ่ง (เช่น 300ms) ก่อนยิงใหม่ เพื่อให้หลุดจาก cache เดิม
//       await new Promise(resolve => setTimeout(resolve, 300)); 
//       return fetchPolygonSnapshotPriceses(symbols, retries - 1);
//     }

//     const symbolsToRetry: string[] = [];

//     // ประมวลผลตามปกติ (ใช้ Logic เดิมที่คัดกรองละเอียด)
//     (data.tickers || []).forEach((ticker: any) => {
//       let currentPrice = 0;
//       if (ticker.day?.c && ticker.day.c > 0) {
//         currentPrice = ticker.day.c;
//       } else if (ticker.lastTrade?.p && ticker.lastTrade.p > 0) {
//         currentPrice = ticker.lastTrade.p;
//       } else if (ticker.prevDay?.c && ticker.prevDay.c > 0) {
//         currentPrice = ticker.prevDay.c; 
//       }

//       if (currentPrice > 0) {
//         prices[ticker.ticker] = currentPrice;
//       } else {
//         symbolsToRetry.push(ticker.ticker);
//       }
//     });

//     if (symbolsToRetry.length > 0) {
//       await Promise.all(
//         symbolsToRetry.map(async (symbol) => {
//           const fallbackPrice = await fetchPolygonLastTrade(symbol);
//           if (fallbackPrice && fallbackPrice > 0) {
//             prices[symbol] = fallbackPrice;
//           }
//         })
//       );
//     }

//     return prices;

//   } catch (err) {
//     console.error('Error fetching batch prices:', err);
//     return {};
//   }
// }
export async function fetchPolygonSnapshotPriceses(symbols: string[]): Promise<{ [symbol: string]: number }> {
  // แยก crypto ไปยิง /v1/crypto/prices ห้ามส่ง X:* เข้า route หุ้น
  const cryptoSymbols = symbols.filter(isCryptoSymbol);
  const stockSymbols = symbols.filter(s => !isCryptoSymbol(s));
  if (cryptoSymbols.length > 0) {
    const [stockPrices, cryptoPrices] = await Promise.all([
      fetchPolygonSnapshotPriceses(stockSymbols),
      fetchCryptoPrices(cryptoSymbols),
    ]);
    return { ...stockPrices, ...cryptoPrices };
  }

  try {
    const prices: { [symbol: string]: number } = {};
    if (symbols.length === 0) return prices;

    const authToken = getToken();
    const symbolQuery = symbols.join(',');
    const res = await fetch(
      `${GATEWAY_BASE_URL}/v1/stocks/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${symbolQuery}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    const data = await res.json();

    if (data.success && data.results) {
      data.results.forEach((tickerData: any) => {
        const sym = tickerData.ticker || tickerData.price?.ticker;
        let currentPrice = 0;

        const regularClose = tickerData.price?.regular_close || 0;
        const extendedPrice = tickerData.price?.extended_price || 0;

        // ลำดับการเลือกราคา
        if (regularClose > 0) {
          currentPrice = regularClose;
        } else if (tickerData.price?.price && tickerData.price.price > 0) {
          currentPrice = tickerData.price.price;
        } else if (extendedPrice > 0) {
          currentPrice = extendedPrice;
        } else if (tickerData.day?.c && tickerData.day.c > 0) {
          currentPrice = tickerData.day.c;
        } else if (tickerData.prevDay?.c && tickerData.prevDay.c > 0) {
          currentPrice = tickerData.prevDay.c;
        }

        if (currentPrice > 0 && sym) {
          prices[sym] = currentPrice;

          // 🌟 แอบเก็บรายละเอียดตลาดลง Cache กลางของไฟล์ไว้ให้โดยอัตโนมัติ (ไม่ต้องยิงซ้ำ)
          marketDetailsCache[sym] = {
            marketSession: tickerData.price?.market_session || null,
            extendedPrice: extendedPrice > 0 ? extendedPrice : null,
            regularClose: regularClose > 0 ? regularClose : null,
            providerTimestampIso: tickerData.price?.provider_timestamp_iso || null, // ✅ เพิ่มบรรทัดนี้
          };
        }
      });
    }

    return prices;
  } catch (err) {
    console.error('Error fetching batch prices via gateway:', err);
    return {};
  }
}

// 🌟 2. ฟังก์ชันเสริมสำหรับดึง Market Details โดยดึงจาก Cache ทันทีโดยไม่ต้องยิง API ใหม่
export function getStoredMarketDetails(symbol: string) {
  if (isCryptoSymbol(symbol)) {
    const crypto = getStoredCryptoDetails(symbol);
    return { marketSession: 'CRYPTO_24_7', extendedPrice: null, regularClose: null, providerTimestampIso: crypto.providerTimestampIso };
  }
  const cached = marketDetailsCache[symbol];
  return cached || { marketSession: null, extendedPrice: null, regularClose: null, providerTimestampIso: null };
}

export async function fetchMasterAssetDetails(symbols: string | string[]): Promise<any[]> {
  if (typeof symbols === 'string' && isCryptoSymbol(symbols)) {
    const name = await fetchCryptoName(symbols);
    return [{ symbol: symbols, name }];
  }

  try {
    const authToken = getToken();
    const symbolQuery = Array.isArray(symbols) ? symbols.join(',') : symbols;

    if (!symbolQuery) return [];

    const url = `${API}/v2/master-assets?ticker=${encodeURIComponent(symbolQuery)}`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`Gateway error: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();

    // ✅ แกะตามโครงสร้างจริง: data.data.items
    const items = data?.data?.items || data?.results || data?.data?.results || [];

    // Map ให้ชื่อฟิลด์รองรับทั้งแบบเก่าและแบบใหม่ (เช่น displayName หรือ shortName)
    return items.map((item: any) => ({
      symbol: item.ticker || item.assetSymbol,
      name: item.displayName || item.shortName || item.name
    }));
  } catch (err) {
    console.error('Error fetching master asset details via gateway:', err);
    return [];
  }
}